import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Box, ButtonBase, Stack, Typography } from "@mui/material";
import { darken, lighten } from "@mui/material/styles";
import ColoredIcon from "../../ColoredIcon";
import DefaultIcon from "../../../img/default_icon.svg";
import BackIcon from "../../../img/Arrow_Left.svg";
import ImagePicker from "../../ImagePicker";
import Color from "../../../modules/Color";
import Brand from "../../../api/Brand";
import { useCookies } from "react-cookie";
import {
    getCloudinaryOriginalUrl,
    uploadToCloudinary,
} from "../../../utils/cloudinaryUtils";
import BottomButton from "../../BottomButton";
import { useNavigate } from "react-router-dom";
import { FullScreenLoader } from "../../FullScreenLoader";
import Footer from "../Footer";
import ImageCropperModal from "../../ImageCropperModal";
import { FONTS } from "../../../constants/styles";

const BrandCreation = () => {
    const navigate = useNavigate();
    const [brand, setBrand] = useState<Brand | undefined>();
    const [name, setName] = useState("");
    const [logo, setLogo] = useState<string | null | undefined>();
    const [logoPreview, setLogoPreview] = useState<string | undefined>();
    const [, setIsBrandingSetUp] = useState(true);

    const colors: { color: Color; name: string }[] = [
        { color: new Color(139, 105, 105), name: "Rosy Brown" },
        { color: new Color(205, 175, 149), name: "Peru" },
        { color: new Color(205, 155, 155), name: "Light Coral" },
        { color: new Color(255, 182, 193), name: "Light Pink" },
        { color: new Color(139, 102, 139), name: "Dark Orchid" },
        { color: new Color(205, 150, 205), name: "Orchid" },
        { color: new Color(139, 58, 98), name: "Dark Magenta" },
        { color: new Color(139, 35, 35), name: "Firebrick" },
        { color: new Color(139, 87, 66), name: "Saddle Brown" },
        { color: new Color(255, 130, 71), name: "Coral" },
        { color: new Color(69, 139, 116), name: "Sea Green" },
        { color: new Color(36, 163, 148), name: "Teal" },
        { color: new Color(66, 159, 169), name: "Cadet Blue" },
        { color: new Color(139, 134, 78), name: "Dark Khaki" },
        { color: new Color(255, 99, 71), name: "Tomato" },
        { color: new Color(139, 58, 58), name: "Indian Red" },
        { color: new Color(255, 0, 0), name: "Red" },
        { color: new Color(16, 78, 139), name: "Dark Blue" },
        { color: new Color(205, 85, 85), name: "Crimson" },
        { color: new Color(30, 144, 255), name: "Dodger Blue" },
        { color: new Color(238, 99, 99), name: "Light Salmon" },
    ];

    const [selectedColor, setSelectedColor] = useState(colors[0].color);
    const [shouldPulse, setShouldPulse] = useState(false);
    const [, setCookie] = useCookies(["theme_color"]);
    const [cookies] = useCookies(["uuid", "brand_id"]);
    const { uuid, brand_id } = cookies;

    // crop modal state
    const [cropOpen, setCropOpen] = useState(false);
    const [cropSourceUrl, setCropSourceUrl] = useState<string | null>(null);

    // derive original for recropping (strip transforms)
    const originalForCrop = useMemo(() => {
        return logoPreview ? getCloudinaryOriginalUrl(logoPreview) : null;
    }, [logoPreview]);

    useEffect(() => {
        if (!uuid || !brand_id) return;
        Brand.info(uuid, brand_id)
            .then((brand) => {
                if (!brand) return;
                setBrand(brand);
                setName(brand.name);
                setLogoPreview(
                    brand.logo ||
                        sessionStorage.getItem(`${brand.key}_image`) ||
                        undefined
                );
                setSelectedColor(
                    new Color(brand.colorRed, brand.colorGreen, brand.colorBlue)
                );
                setIsBrandingSetUp(!!brand.setupSteps.branding);
            })
            .catch(console.error);
    }, [uuid, brand_id]);

    useEffect(() => {
        setCookie("theme_color", selectedColor.toHex(), { domain: "/" });
    }, [setCookie, selectedColor]);

    useEffect(() => {
        const timer = setTimeout(() => {
            if (!brand?.setupSteps.branding) {
                setShouldPulse(true);
            }
        }, 1000);

        return () => clearTimeout(timer);
    }, [brand?.setupSteps.branding]);

    const onSaveOrExit = useCallback(async () => {
        if (!uuid || !brand_id || !brand) return;

        console.log(selectedColor);

        Brand.updateBranding(uuid, brand_id, {
            colorRed: selectedColor.r,
            colorGreen: selectedColor.g,
            colorBlue: selectedColor.b,
            logo,
        })
            .then(() => navigate("/"))
            .catch(console.error);
    }, [uuid, brand_id, brand, logo, selectedColor, navigate]);

    /*
    // No removal button yet
    const onLogoRemove = () => {
        setLogo(null);
        setLogoPreview(undefined);
        sessionStorage.removeItem(`${brand!.key}_image`);
    }; */

    /*
    const isHttpUrl = (s: string) => {
        try {
            const u = new URL(s);
            return u.protocol === "http:" || u.protocol === "https:";
        } catch {
            return false;
        }
    };
    */

    const isDataUrl = (s: string) => /^data:image\/[a-zA-Z]+;base64,/.test(s);

    const ensureDataUrl = (s: string) =>
        isDataUrl(s) ? s : `data:image/png;base64,${s}`;

    /**
     * Accepts either:
     * - base64/data URL from user file input -> uploads to Cloudinary (staging), or
     * - remote URL from AI generation -> uploads to Cloudinary (staging), or
     * - ready URL from server -> uses directly (no re-upload).
     */
    const onLogoUpload = async (input?: string) => {
        if (!input) return;

        // Remote URLs (e.g. DALL-E) and base64 data URLs both go to Cloudinary staging.
        // Cloudinary accepts remote URLs directly in the file field.
        try {
            const isRemoteUrl = /^https?:\/\//.test(input);
            const file = isRemoteUrl ? input : ensureDataUrl(input);
            const stagingUrl = await uploadToCloudinary(file);
            if (stagingUrl) {
                setCropSourceUrl(stagingUrl);
                setCropOpen(true);
            } else {
                console.error("Failed to upload logo to Cloudinary.");
            }
        } catch (error) {
            console.error("Error during logo upload:", error);
        }
    };

    // When user confirms crop, we receive a Cloudinary URL with transforms
    const handleCropSave = (finalTransformedUrl: string) => {
        setLogo(finalTransformedUrl); // this is what goes to BE on Save
        setLogoPreview(finalTransformedUrl); // this is what we render immediately in UI
        setCropOpen(false);
        setCropSourceUrl(null);
    };

    // When user cancels crop (keeps current avatar untouched)
    const handleCropCancel = () => {
        setCropOpen(false);
        setCropSourceUrl(null);
    };

    const hex = selectedColor.toHex();

    if (brand === undefined) return <FullScreenLoader />;
    return (
        <>
            <Stack
                direction="column"
                width="100%"
                flex={1}
                sx={{
                    overflowY: "auto",
                    overflowX: "hidden",
                    bgcolor: "white",
                }}
            >
                {/* ── Hero ── */}
                <Stack
                    width="100%"
                    alignItems="center"
                    sx={{
                        background: `linear-gradient(180deg, ${lighten(hex, 0.08)} 0%, ${darken(hex, 0.08)} 100%)`,
                        pt: "48px",
                        pb: "48px",
                        px: 2,
                        boxSizing: "border-box",
                        position: "relative",
                        transition: "background 0.4s ease",
                        "&::after": {
                            content: '""',
                            position: "absolute",
                            inset: 0,
                            opacity: 0.04,
                            backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.65' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E")`,
                            backgroundRepeat: "repeat",
                            backgroundSize: "128px 128px",
                            pointerEvents: "none",
                        },
                    }}
                >
                    {/* Back arrow */}
                    <ButtonBase
                        onClick={() => navigate(-1 as any)}
                        sx={{
                            position: "absolute",
                            top: 12,
                            left: 10,
                            zIndex: 2,
                            width: 36,
                            height: 36,
                            borderRadius: "50%",
                            bgcolor: "rgba(60,60,60,0.35)",
                            backdropFilter: "blur(16px)",
                            WebkitBackdropFilter: "blur(16px)",
                            border: "1px solid rgba(255,255,255,0.2)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                        }}
                    >
                        <ColoredIcon
                            icon={BackIcon}
                            color="white"
                            size="28px"
                        />
                    </ButtonBase>

                    {/* Icon preview */}
                    <Box
                        onClick={() => {
                            if (!logoPreview) return;
                            setCropSourceUrl(
                                originalForCrop ?? logoPreview
                            );
                            setCropOpen(true);
                        }}
                        sx={{
                            width: 96,
                            height: 96,
                            borderRadius: "22px",
                            bgcolor: "rgba(60,60,60,0.3)",
                            backdropFilter: "blur(16px)",
                            WebkitBackdropFilter: "blur(16px)",
                            border: "1px solid rgba(255,255,255,0.15)",
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            cursor: logoPreview ? "pointer" : "default",
                            boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
                            position: "relative",
                            zIndex: 1,
                        }}
                    >
                        {logoPreview ? (
                            <Box
                                width="100%"
                                height="100%"
                                borderRadius="22px"
                                sx={{
                                    backgroundImage: `url(${logoPreview})`,
                                    backgroundPosition: "center",
                                    backgroundRepeat: "no-repeat",
                                    backgroundSize: "cover",
                                }}
                            />
                        ) : (
                            <ColoredIcon
                                icon={DefaultIcon}
                                size={64}
                                color="white"
                            />
                        )}
                    </Box>

                    {/* Brand name + hint — glass card */}
                    <Stack
                        alignItems="center"
                        spacing={0.5}
                        sx={{
                            bgcolor: "rgba(255,255,255,0.18)",
                            backdropFilter: "blur(16px)",
                            WebkitBackdropFilter: "blur(16px)",
                            border: "1px solid rgba(255,255,255,0.3)",
                            borderRadius: "16px",
                            px: 2,
                            py: 1.25,
                            mt: 1.5,
                            position: "relative",
                            zIndex: 1,
                        }}
                    >
                        <Typography
                            className="no-translation"
                            fontWeight={600}
                            fontSize="20px"
                            lineHeight={1.3}
                            textAlign="center"
                            fontFamily={FONTS.display}
                            color="white"
                        >
                            {name}
                        </Typography>
                        <Typography
                            fontSize="13px"
                            lineHeight="18px"
                            color="rgba(255,255,255,0.7)"
                            textAlign="center"
                        >
                            This is how your brand will appear
                        </Typography>
                    </Stack>
                </Stack>

                {/* ── Bottom sheet ── */}
                <Stack
                    sx={{
                        bgcolor: "white",
                        borderTopLeftRadius: "24px",
                        borderTopRightRadius: "24px",
                        marginTop: "-24px",
                        position: "relative",
                        zIndex: 1,
                        px: 2,
                        pt: 3,
                        pb: "180px",
                        boxSizing: "border-box",
                        "@keyframes fadeSlideIn": {
                            from: {
                                opacity: 0,
                                transform: "translateY(12px)",
                            },
                            to: { opacity: 1, transform: "translateY(0)" },
                        },
                        animation: "fadeSlideIn 0.3s ease-out",
                    }}
                    spacing={3}
                >
                    {/* APP ICON section */}
                    <Stack spacing={1.5} width="100%">
                        <Typography
                            fontSize="11px"
                            fontWeight={600}
                            color="text.secondary"
                            textTransform="uppercase"
                            letterSpacing="0.5px"
                        >
                            App Icon
                        </Typography>
                        <Stack alignItems="center">
                            <ImagePicker
                                withAI
                                onUpload={onLogoUpload}
                                customColor={selectedColor.toHex()}
                                shouldPulse={shouldPulse}
                                brandName={name}
                                businessType={brand.business || ""}
                                brandColor={selectedColor}
                            />
                        </Stack>
                    </Stack>

                    {/* BRAND COLOR section */}
                    <Stack spacing={1.5} width="100%">
                        <Typography
                            fontSize="11px"
                            fontWeight={600}
                            color="text.secondary"
                            textTransform="uppercase"
                            letterSpacing="0.5px"
                        >
                            Brand Color
                        </Typography>
                        <Box
                            sx={{
                                display: "flex",
                                flexWrap: "wrap",
                                justifyContent: "center",
                                gap: "12px",
                                py: 1,
                            }}
                        >
                            {colors.map(({ color, name }) => (
                                <ColorCircle
                                    key={color.toHex()}
                                    color={color}
                                    label={name}
                                    isSelected={color.cmp(selectedColor)}
                                    onClick={(c) => setSelectedColor(c)}
                                />
                            ))}
                        </Box>
                    </Stack>
                </Stack>
            </Stack>

            {/* Image crop modal */}
            <ImageCropperModal
                open={cropOpen}
                src={cropSourceUrl}
                onCancel={handleCropCancel}
                onSave={handleCropSave}
                //onDelete={}
                mode="square"
                outSize={{ w: 512, h: 512 }}
            />
            <BottomButton
                sticky
                footer
                onClick={onSaveOrExit}
                customColor={selectedColor.toHex()}
            >
                {/* isFirstSetup ? "Go to profile tuning" : "Save Changes" */}
                Save Changes
            </BottomButton>
            <Footer tab="admin" />
        </>
    );
};

const ColorCircle = (props: {
    color: Color;
    label: string;
    isSelected?: boolean;
    onClick?: (color: Color) => void;
}) => {
    const { color, label, isSelected, onClick } = props;

    return (
        <Stack alignItems="center" spacing={0.5}>
            <Box sx={{ width: 48, height: 48, display: "flex", alignItems: "center", justifyContent: "center" }}>
                <ButtonBase
                    onClick={() => onClick?.(color)}
                    sx={{
                        width: 40,
                        height: 40,
                        borderRadius: "50%",
                        bgcolor: color.toHex(),
                        outline: isSelected
                            ? `3px solid ${color.toHex()}`
                            : "3px solid transparent",
                        outlineOffset: "3px",
                        transition: "outline 0.15s ease",
                    }}
                />
            </Box>
            <Typography
                fontSize="10px"
                lineHeight="12px"
                textAlign="center"
                color="text.secondary"
                width={50}
            >
                {label}
            </Typography>
        </Stack>
    );
};

export default BrandCreation;
